import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Phone, PhoneIncoming, PhoneMissed, Clock, BarChart3, Loader2, Play, Tag } from 'lucide-react';
import WebsiteTracking from './WebsiteTracking';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ClientCallTracking({ client, getHeaders }) {
  const { t } = useTranslation('dashboard');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(() => {
    setLoading(true);
    fetch(`${API}/api/client/dashboard/call-tracking`, { headers: getHeaders() })
      .then(r => r.json())
      .then(d => setData(d))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [getHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const formatDuration = (s) => {
    if (!s) return '0:00';
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${String(sec).padStart(2, '0')}`;
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-blue-400 animate-spin" /></div>;

  const connected = data?.connected;
  const stats = data?.stats || {};
  const calls = data?.recent_calls || [];
  const channels = data?.channel_breakdown || {};
  const trackingNumbers = data?.tracking_numbers || [];

  return (
    <div className="space-y-4" data-testid="call-tracking-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Phone className="w-5 h-5 text-green-500" />
          <h2 className="text-lg font-semibold text-gray-900">{t('callTracking.title')}</h2>
          <Badge className={connected ? 'bg-green-50 text-green-600 text-[10px]' : 'bg-yellow-50 text-yellow-600 text-[10px]'}>
            {connected ? t('callTracking.active') : t('callTracking.pendingSetup')}
          </Badge>
        </div>
      </div>

      {/* Not connected */}
      {!connected && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="py-10 text-center">
            <Phone className="w-10 h-10 text-blue-400 mx-auto mb-3" />
            <h3 className="text-sm font-semibold text-blue-900 mb-1">{t('callTracking.settingUpTitle')}</h3>
            <p className="text-xs text-blue-700 max-w-md mx-auto">
              {t('callTracking.settingUpDesc')}
            </p>
          </CardContent>
        </Card>
      )}

      {connected && (
        <>
          {/* Tracking Numbers */}
          {trackingNumbers.length > 0 && (
            <Card className="bg-white border-gray-200">
              <CardContent className="p-4">
                <p className="text-xs text-gray-500 font-medium mb-2">{t('callTracking.yourTrackingNumbers')}</p>
                <div className="space-y-1.5">
                  {trackingNumbers.map((n, i) => (
                    <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-lg px-3 py-2">
                      <PhoneIncoming className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                      <span className="text-sm font-mono font-semibold text-gray-900" data-testid={`tracking-number-${i}`}>{n.number}</span>
                      <Badge className="bg-purple-50 text-purple-600 text-[9px]">
                        <Tag className="w-2.5 h-2.5 mr-0.5 inline" />{n.channel || t('callTracking.mainLine')}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Card className="bg-white border-gray-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Phone className="w-4 h-4 text-blue-500" />
                  <span className="text-xs text-gray-500">{t('callTracking.totalCalls')}</span>
                </div>
                <p className="text-2xl font-bold text-gray-900" data-testid="total-calls">{stats.total_calls || 0}</p>
              </CardContent>
            </Card>
            <Card className="bg-white border-gray-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <PhoneIncoming className="w-4 h-4 text-green-500" />
                  <span className="text-xs text-gray-500">{t('callTracking.answered')}</span>
                </div>
                <p className="text-2xl font-bold text-green-600" data-testid="answered-calls">{stats.answered || 0}</p>
              </CardContent>
            </Card>
            <Card className="bg-white border-gray-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <PhoneMissed className="w-4 h-4 text-red-500" />
                  <span className="text-xs text-gray-500">{t('callTracking.missed')}</span>
                </div>
                <p className="text-2xl font-bold text-red-500" data-testid="missed-calls">{stats.missed || 0}</p>
              </CardContent>
            </Card>
            <Card className="bg-white border-gray-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-4 h-4 text-amber-500" />
                  <span className="text-xs text-gray-500">{t('callTracking.avgDuration')}</span>
                </div>
                <p className="text-2xl font-bold text-amber-600" data-testid="avg-duration">{formatDuration(stats.avg_duration_seconds)}</p>
              </CardContent>
            </Card>
          </div>

          {/* Channel Breakdown */}
          {Object.keys(channels).length > 0 && (
            <Card className="bg-white border-gray-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <BarChart3 className="w-4 h-4 text-blue-500" />
                  <h3 className="text-sm font-semibold text-gray-900">{t('callTracking.callsByChannel')}</h3>
                </div>
                <div className="space-y-2">
                  {Object.entries(channels).sort((a, b) => b[1] - a[1]).map(([ch, count]) => (
                    <div key={ch} className="flex items-center justify-between">
                      <span className="text-xs text-gray-600 flex items-center gap-1.5">
                        <Tag className="w-3 h-3 text-purple-400" />{ch}
                      </span>
                      <div className="flex items-center gap-2">
                        <div className="w-28 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-purple-500 rounded-full" style={{ width: `${Math.round(count / stats.total_calls * 100)}%` }} />
                        </div>
                        <span className="text-xs text-gray-900 font-medium w-8 text-right">{count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Website Tracking (DNI) */}
          <WebsiteTracking client={client} getHeaders={getHeaders} />

          {/* Recent Calls Table */}
          <Card className="bg-white border-gray-200">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('callTracking.recentCalls')}</h3>
              {calls.length === 0 ? (
                <div className="text-center py-8">
                  <Phone className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">{t('callTracking.noCallsYet')}</p>
                  <p className="text-xs text-gray-400 mt-1">{t('callTracking.noCallsHint')}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="calls-table">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left text-gray-500 py-2 pr-4 font-medium">{t('callTracking.tableCaller')}</th>
                        <th className="text-left text-gray-500 py-2 pr-4 font-medium">{t('callTracking.tableChannel')}</th>
                        <th className="text-left text-gray-500 py-2 pr-4 font-medium">{t('callTracking.tableDuration')}</th>
                        <th className="text-left text-gray-500 py-2 pr-4 font-medium">{t('callTracking.tableStatus')}</th>
                        <th className="text-left text-gray-500 py-2 pr-4 font-medium">{t('callTracking.tableRecording')}</th>
                        <th className="text-left text-gray-500 py-2 font-medium">{t('callTracking.tableTime')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {calls.map((c) => (
                        <tr key={c.call_id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-2.5 pr-4 text-gray-900 font-mono">{c.caller_id || t('callTracking.unknownCaller')}</td>
                          <td className="py-2.5 pr-4">
                            <Badge className="bg-purple-50 text-purple-600 text-[9px]">{c.channel || t('callTracking.direct')}</Badge>
                          </td>
                          <td className="py-2.5 pr-4 text-gray-700">{formatDuration(c.call_duration)}</td>
                          <td className="py-2.5 pr-4">
                            <Badge className={`text-[9px] ${
                              c.call_status === 'completed' || c.call_duration > 0
                                ? 'bg-green-50 text-green-600'
                                : 'bg-red-50 text-red-500'
                            }`}>
                              {c.call_status === 'completed' || c.call_duration > 0 ? t('callTracking.answeredStatus') : t('callTracking.missedStatus')}
                            </Badge>
                          </td>
                          <td className="py-2.5 pr-4">
                            {c.recording_url ? (
                              <a href={c.recording_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-500 hover:text-blue-700">
                                <Play className="w-3 h-3" /> {t('callTracking.play')}
                              </a>
                            ) : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="py-2.5 text-gray-500">{c.received_at ? new Date(c.received_at).toLocaleString() : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
